import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import type {
  SimulateScenarioRequest,
  ScenarioResultResponse,
} from '@/types/api'

// =============================================================================
// Historical Episodes
// =============================================================================

export interface HistoricalEpisode {
  id: string
  name: string
  description: string
  referencePeriod: string
  startDate: string
  endDate: string
}

const HISTORICAL_EPISODES: HistoricalEpisode[] = [
  {
    id: 'COVID Mar 2020',
    name: 'COVID Mar 2020',
    description: 'The COVID-19 pandemic crash saw global markets plummet as lockdowns began',
    referencePeriod: 'Feb 19 - Mar 23, 2020',
    startDate: '2020-02-19',
    endDate: '2020-03-23',
  },
  {
    id: 'Q4 2018 Deleveraging',
    name: 'Q4 2018 Deleveraging',
    description: 'Fed tightening, trade war concerns, and systematic deleveraging led to sharp correction',
    referencePeriod: 'Sep 20 - Dec 24, 2018',
    startDate: '2018-09-20',
    endDate: '2018-12-24',
  },
  {
    id: '2021-22 Rate Hike Cycle',
    name: '2021-22 Rate Hike Cycle',
    description: 'Federal Reserve tightening cycle with aggressive rate increases',
    referencePeriod: 'Nov 2021 - Oct 2022',
    startDate: '2021-11-10',
    endDate: '2022-10-13',
  },
  {
    id: 'LUNA/3AC Crash Jun 2022',
    name: 'LUNA/3AC Crash Jun 2022',
    description: 'Collapse of Terra/LUNA and Three Arrows Capital contagion',
    referencePeriod: 'May 9 - Jun 18, 2022',
    startDate: '2022-05-05',
    endDate: '2022-06-18',
  },
  {
    id: 'ftx-collapse-nov-2022',
    name: 'FTX Collapse Nov 2022',
    description: 'Fall of FTX exchange and subsequent crypto credit contagion',
    referencePeriod: 'Nov 2 - Nov 14, 2022',
    startDate: '2022-11-02',
    endDate: '2022-11-14',
  },
]

/**
 * Get list of historical episodes for Historical Replay mode
 * Returns static list (backend endpoint optional enhancement)
 */
export function useHistoricalEpisodes() {
  return useQuery({
    queryKey: ['historical-episodes'],
    queryFn: async () => {
      // In the future, this could call a backend endpoint
      // For now, return static list as specified in plan
      return HISTORICAL_EPISODES
    },
    staleTime: Infinity, // Episodes don't change
  })
}

// =============================================================================
// Scenario Simulation
// =============================================================================

/**
 * Run a scenario simulation
 * Mutation hook that posts to /api/scenarios/simulate
 */
export function useSimulateScenario() {
  return useMutation({
    mutationKey: ['scenario', 'simulate'],
    mutationFn: async (request: SimulateScenarioRequest): Promise<ScenarioResultResponse> => {
      return apiClient.simulateScenario(request)
    },
    // Cache successful results for 5 minutes
    gcTime: 1000 * 60 * 5,
  })
}
